import React, { useState, useEffect } from 'react';
import { Grid, Button, Box } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { green, red } from '@material-ui/core/colors';
import moment from 'moment';

// @design-system
import Table from '../../../../design-system/components/Table/Table';
import LoaderContent from '../../../../design-system/components/Loader/LoaderContent';
import ChipFilter from '../../../../design-system/components/Chip/ChipFilter';
import Select from '../../../../design-system/components/Form/Select/Select';
import NothingFound from '../../../../components/NothingFound/NothingFound';
import MoreOptions from '../../../../design-system/components/MoreOptions/MoreOptions';
import TableFilter from '../../../../design-system/components/Table/TableFilter/TableFilter';
import SearchByInput from '../../../../design-system/components/Table/TableFilter/components/SearchByInput/SearchByInput';
import { modificationsSearchColumns } from '../ColumnsTable';
import { MEMBERSHIPS, MEMBERSHIPS_NAME } from '../../../../design-system/constants/memberships';

// @context
import { useB2BPermissions } from '../../../../context/B2BPermissionsContext';

// Nombre canónico de la membresía Freemium (ej. "Freemium"). Se usa para decidir
// qué acciones de modificación aplican. Un alumno Freemium no tiene una membresía
// de paga vigente, por lo que "Procesar Baja" y "Extender" no tienen sentido:
// solo se permite "Cambiar Membresía" (upgrade).
const FREEMIUM_NAME = MEMBERSHIPS_NAME[MEMBERSHIPS.FREEMIUM];

const SEARCH_ITEMS = [
    { id: 'person_id', label: 'ID', placeholder: 'Escribe el ID' },
    { id: 'user_id', label: 'Matrícula', placeholder: 'Escribe la matrícula' },
    { id: 'first_name', label: 'Nombre', placeholder: 'Escribe el nombre' },
    { id: 'email', label: 'Correo', placeholder: 'Escribe el correo' },
];

// Valor del selector que representa "todas las empresas del scope del usuario".
const ALL_COMPANIES = '__ALL__';

const StudentSearch = ({ students, loading, error, totalPages, page, onSearch, onPageChange, onSelectStudent, refreshSignal }) => {
    // Empresas configuradas del usuario (de su scope activo: internal o external).
    // `autoSelectCompany` !== null cuando el usuario tiene UNA sola empresa externa:
    // en ese caso el selector debe venir precargado y seleccionado con ella.
    const { assignedCompanies, autoSelectCompany } = useB2BPermissions();

    const [searchFilter, setSearchFilter] = useState({
        searchType: 'person_id',
        search: '',
    });
    const [filters, setFilters] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});

    // Empresa seleccionada en el dropdown. ALL_COMPANIES = buscar en todas las
    // empresas del scope (manda un empresa_tag por cada una). Una empresa
    // específica = acota la búsqueda a esa sola. El selector se alimenta SOLO con
    // las empresas asignadas al usuario, sea su scope internal o external.
    const [selectedCompany, setSelectedCompany] = useState(ALL_COMPANIES);

    // Opciones del selector: "Todas" + cada empresa del scope. El value es el
    // company_name porque es lo que la lambda espera como empresa_tag.
    const companyItems = [
        { id: ALL_COMPANIES, label: 'Todas las empresas' },
        ...assignedCompanies.map((c) => ({ id: c.company_name, label: c.company_name })),
    ];

    // Traduce la selección del dropdown al empresa_tag que recibe searchStudents:
    // null = todas; string = esa empresa.
    const resolveCompanyTag = (value) => (value === ALL_COMPANIES ? null : value);

    // Búsqueda inicial. Si NO hay autoselección (caso normal), busca en todas las
    // empresas del scope. Si SÍ hay autoselección (una sola empresa externa), se
    // omite esta búsqueda "todas": el effect de autoselección de abajo dispara la
    // búsqueda acotada a esa empresa, evitando una consulta duplicada.
    useEffect(() => {
        if (autoSelectCompany) return;
        onSearch([], 1, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Autoselección: cuando el usuario tiene una sola empresa externa, el contexto
    // de permisos (carga async) entrega `autoSelectCompany`. Al llegar, se fija el
    // selector a esa empresa y se ejecuta la búsqueda acotada a ella. El selector
    // usa `company_name` como value (es lo que la lambda espera como empresa_tag).
    useEffect(() => {
        if (!autoSelectCompany) return;
        setSelectedCompany(autoSelectCompany.company_name);
        onPageChange(1);
        onSearch(filters, 1, autoSelectCompany.company_name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSelectCompany]);

    // Recargar los datos tras una modificación exitosa (baja/extensión/cambio)
    // SIN perder los filtros: re-ejecuta la última búsqueda con los filtros,
    // empresa y página que el usuario tenía aplicados. El padre incrementa
    // `refreshSignal` después de cada POST exitoso. Se omite el valor inicial (0)
    // para no duplicar la búsqueda del mount.
    useEffect(() => {
        if (!refreshSignal) return;
        onSearch(filters, page, resolveCompanyTag(selectedCompany));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    const handleChangeCompany = (e) => {
        const value = e.target.value;
        setSelectedCompany(value);
        onPageChange(1);
        onSearch(filters, 1, resolveCompanyTag(value));
    };

    const handleChangeSearch = (obj) => {
        const { name, value } = obj;
        setSearchFilter({
            searchType: name,
            search: value,
        });
    };

    const handleClickDeleteSearch = (searchType) => {
        setSearchFilter({
            searchType: searchType || searchFilter.searchType,
            search: '',
        });
        // If clearing input, re-fetch with current filters
    };

    const handleSearch = () => {
        if (searchFilter.search.trim()) {
            const newFilter = {};
            newFilter[searchFilter.searchType] = searchFilter.search.trim();
            newFilter['label'] = `${SEARCH_ITEMS.find(i => i.id === searchFilter.searchType)?.label || searchFilter.searchType}: ${searchFilter.search}`;

            // Remove existing filter of same type if any
            const newFilters = filters.filter(f => !f[searchFilter.searchType]);
            newFilters.push(newFilter);

            setFilters(newFilters);
            setSelectedFilters({ ...selectedFilters, [searchFilter.searchType]: searchFilter.search.trim() });
            setSearchFilter({ searchType: 'person_id', search: '' });
            onPageChange(1);
            onSearch(newFilters, 1, resolveCompanyTag(selectedCompany));
        }
    };

    const handleDeleteChip = (chipToDelete) => () => {
        const newFilters = filters.filter((chip) => JSON.stringify(chip) !== JSON.stringify(chipToDelete));
        setFilters(newFilters);
        const newSelectedFilters = { ...selectedFilters };
        const value = Object.keys(chipToDelete).find(k => k !== 'label');
        if (value) delete newSelectedFilters[value];
        setSelectedFilters(newSelectedFilters);
        onPageChange(1);
        onSearch(newFilters, 1, resolveCompanyTag(selectedCompany));
    };

    const handleChangePage = (e, newPage) => {
        e.preventDefault();
        onPageChange(newPage);
        onSearch(filters, newPage, resolveCompanyTag(selectedCompany));
    };

    const handleAction = (student, action) => {
        onSelectStudent({ ...student, defaultAction: action });
    };

    // Construye las opciones del menú de acciones según la membresía del alumno.
    // Freemium: solo "Cambiar Membresía" (no hay membresía de paga que dar de baja
    // ni extender). Resto de membresías: las tres acciones. La comparación es
    // case-insensitive y tolerante a espacios para no romperse ante variaciones
    // de capitalización en el nombre que devuelve la lambda.
    const buildActionOptions = (it) => {
        const changeOption = { id: 3, label: 'Cambiar Membresía', fun: () => handleAction(it, 'cambio'), icon: <i className="ri-exchange-line"></i> };
        // Reenvío de correo: aplica a cualquier alumno (incluido Freemium), ya que
        // reenvía el correo al destinatario de student.email.
        const resendEmailOption = { id: 4, label: 'Envío de Correo', fun: () => handleAction(it, 'reenvio_correo'), icon: <i className="ri-mail-send-line"></i> };

        const isFreemium = String(it.membership_name || '').trim().toLowerCase() === String(FREEMIUM_NAME || '').toLowerCase();
        if (isFreemium) {
            // Freemium: solo cambiar membresía (sin baja/extender/envío de correo).
            return [changeOption];
        }

        return [
            { id: 1, label: 'Procesar Baja', fun: () => handleAction(it, 'baja'), icon: <i className="ri-close-circle-line"></i> },
            { id: 2, label: 'Extender', fun: () => handleAction(it, 'extension'), icon: <i className="ri-time-line"></i> },
            changeOption,
            resendEmailOption,
        ];
    };

    // Format rows for table display
    const rows = students.map((it, index) => ({
        ...it,
        id_link: String(it.id),
        full_name: `${it.first_name || ''} ${it.last_name || ''}`.trim(),
        user_id: it.user_id ?? '-',
        company_name: it.empresa || '-',
        membership_name: it.membership_name || '-',
        start_date_membership: it.start_date_membership
            ? moment(it.start_date_membership).format('DD/MM/YYYY')
            : '-',
        end_date_membership: it.end_date_membership
            ? moment(it.end_date_membership).format('DD/MM/YYYY')
            : '-',
        is_active_icon: (
            <span
                key={index}
                style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: it.status_membership === 'Activo' ? green[500] : red[500],
                }}
            />
        ),
        actions: (
            <MoreOptions
                options={buildActionOptions(it)}
                ExAction={true}
            />
        ),
    }));

    return (
        <>
            <TableFilter>
                <Grid item xs style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Box width="100%">
                        <SearchByInput
                            selectValue={searchFilter.searchType}
                            searchValue={searchFilter.search}
                            items={SEARCH_ITEMS}
                            onChange={handleChangeSearch}
                            onKeyDown={handleSearch}
                            onClickDelete={handleClickDeleteSearch}
                            selectContainerProps={{ maxWidth: 280 }}
                        />
                    </Box>
                </Grid>
                {assignedCompanies.length > 0 && (
                    <Grid item style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Box width={220}>
                            <Select
                                items={companyItems}
                                margin="dense"
                                variant="outlined"
                                label="Empresa"
                                name="companyId"
                                onChange={handleChangeCompany}
                                value={selectedCompany}
                                size="medium"
                                fullWidth
                            />
                        </Box>
                    </Grid>
                )}
                <Grid item xs={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="contained" color="primary" fullWidth onClick={handleSearch}>
                        Buscar
                    </Button>
                </Grid>
            </TableFilter>
            <Grid container spacing={1} style={{ padding: '0 16px' }}>
                <Grid item xs={12}>
                    <ChipFilter chipData={filters} handleDelete={handleDeleteChip} />
                </Grid>
            </Grid>
            <LoaderContent loading={loading} minHeight={200}>
                {error && !loading && (
                    <NothingFound
                        width="200px"
                        caption="Error en la búsqueda"
                        type="data"
                        subcaption={error}
                    />
                )}
                {!error && students.length > 0 ? (
                    <React.Fragment>
                        <div style={{ float: 'right' }}>
                            {!loading && (
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handleChangePage}
                                    defaultPage={1}
                                    siblingCount={0}
                                    showFirstButton
                                    showLastButton
                                />
                            )}
                        </div>
                        <Table columns={modificationsSearchColumns} rows={rows} />
                        <div style={{ float: 'right' }}>
                            {!loading && (
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handleChangePage}
                                    defaultPage={1}
                                    siblingCount={0}
                                    showFirstButton
                                    showLastButton
                                />
                            )}
                        </div>
                    </React.Fragment>
                ) : (
                    !loading && !error && (
                        <NothingFound
                            width="200px"
                            caption="No se encontraron registros que coincidan con el criterio de búsqueda ingresado."
                            type="data"
                            subcaption="Intenta cambiando tus criterios."
                        />
                    )
                )}
            </LoaderContent>
        </>
    );
};

export default StudentSearch;
